import { type MutableRefObject, type RefObject, useEffect, useRef } from 'react';
import type { RenderParameters } from 'turnstile-types';

/**
 * Parameters for the {@link useTurnstileWidget} hook.
 */
export interface UseTurnstileWidgetParams {
  /** DOM element where the Cloudflare Turnstile widget will be rendered. */
  containerRef: RefObject<HTMLElement | null>;
  /** Mutable ref that receives the widget ID returned by `turnstile.render`. */
  widgetIdRef: MutableRefObject<string | undefined>;
  /** When false, the widget is not rendered and any previously rendered widget is removed. */
  enabled: boolean;
  /** Cloudflare Turnstile site key. */
  siteKey: string;
  /** Called with the captcha token once the challenge succeeds. */
  onSuccess: (token: string) => void;
}

const SCRIPT_POLL_INTERVAL_MS = 50;

/**
 * Mounts a Cloudflare Turnstile widget into `containerRef` using the official
 * `window.turnstile` JS API. Waits for the script (loaded from `index.html`)
 * if it is not yet available, writes the widget ID into `widgetIdRef`, and
 * removes the widget on cleanup.
 */
export const useTurnstileWidget = ({
  containerRef,
  widgetIdRef,
  enabled,
  siteKey,
  onSuccess,
}: UseTurnstileWidgetParams): void => {
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!enabled || !siteKey) return;
    const container = containerRef.current;
    if (!container) return;

    let widgetId: string | undefined;
    let cancelled = false;
    let pollInterval: ReturnType<typeof setInterval> | undefined;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || widgetId) return;
      const renderParams: RenderParameters = {
        sitekey: siteKey,
        execution: 'execute',
        appearance: 'interaction-only',
        language: 'en',
        callback: (token) => onSuccessRef.current(token),
        'error-callback': () => {
          if (widgetId) window.turnstile?.reset(widgetId);
        },
        'expired-callback': () => {
          if (widgetId) window.turnstile?.reset(widgetId);
        },
      };
      widgetId = window.turnstile.render(container, renderParams);
      widgetIdRef.current = widgetId;
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      pollInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(pollInterval);
          pollInterval = undefined;
          renderWidget();
        }
      }, SCRIPT_POLL_INTERVAL_MS);
    }

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
      if (widgetId) {
        window.turnstile?.remove(widgetId);
        if (widgetIdRef.current === widgetId) {
          widgetIdRef.current = undefined;
        }
      }
    };
  }, [enabled, siteKey, containerRef, widgetIdRef]);
};
